import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { HOSOLUUTRU_API, BaseGatewayImage } from '@app/utils/apiUrl';
import { Get, Post, Put, DeleteSingle } from '@app/utils/httpProvider';
import DocumentInput from '@app/components/document-input/DocumentInput';
import { ConfirmModal } from '../../../../../modules/modal/ConfirmModal';
import { messageToast, status200, loaiTuyenSinhValue } from '../../../../../utils/define';

const HoSoLuuTruModal = ({ isOpen, onClose, dotTuyenSinhId, tenDot, loaiTuyenSinh = loaiTuyenSinhValue.tuyenSinhTienSi }) => {
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    
    const [tenFile, setTenFile] = useState('');
    const [fileList, setFileList] = useState([]);

    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');

    useEffect(() => {
        setIsEditing(false);
        setCurrentRecord(null);
        setTenFile('');
        setFileList([]);
    }, [dotTuyenSinhId, isOpen]);

    const { data, refetch, isLoading } = useQuery(
        ['getHoSoLuuTru', dotTuyenSinhId, loaiTuyenSinh],
        () => {
            if (!dotTuyenSinhId) return Promise.resolve(null);
            return Get(HOSOLUUTRU_API.GET_PAGING, null, {
                DotTuyenSinhId: dotTuyenSinhId,
                LoaiTuyenSinh: loaiTuyenSinh,
                PageIndex: 1,
                PageSize: 1000
            });
        },
        {
            enabled: isOpen && !!dotTuyenSinhId,
            cacheTime: 0,
            staleTime: 0
        }
    );

    const items = data?.data?.$values ? data.data.$values : (data?.data || []);

    const createMutation = useMutation((payload) => Post(HOSOLUUTRU_API.CREATE_ITEM, null, payload));
    const updateMutation = useMutation(({ id, payload }) => Put(HOSOLUUTRU_API.UPDATE_ITEM, id, payload));
    const deleteMutation = useMutation((id) => DeleteSingle(HOSOLUUTRU_API.DELETE_ITEM, id, null));

    const handleAdd = () => {
        setCurrentRecord(null);
        setTenFile('');
        setFileList([]);
        setIsEditing(true);
    };

    const handleEdit = (record) => {
        setCurrentRecord(record);
        setTenFile(record.tenFile || '');
        
        let paths = [];
        let rawPath = record.fileLuuTru;
        if (rawPath) {
            try {
                const parsed = JSON.parse(rawPath);
                if (Array.isArray(parsed)) {
                    paths = parsed;
                } else {
                    paths = [rawPath];
                }
            } catch(e) {
                paths = [rawPath];
            }
            
            const loadedFiles = paths.map(p => {
                let name = p.split('/').pop() || 'File';
                name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                return { filePath: p, name: name };
            });
            setFileList(loadedFiles);
        } else {
            setFileList([]);
        }
        setIsEditing(true);
    };

    const handleDelete = (id) => {
        setIdToDelete(id);
        setOpenConfirmDelete(true);
    };

    const confirmDelete = () => {
        deleteMutation.mutate(idToDelete, {
            onSuccess: (res) => {
                if (res.status === status200 || res.status === 201 || res.status === 204) {
                    toast.success(messageToast.messageToastDeleteSuccess);
                    refetch();
                } else {
                    toast.error(messageToast.messageToastDeleteError);
                }
            },
            onError: () => toast.error(messageToast.messageToastTryCatch),
            onSettled: () => setOpenConfirmDelete(false)
        });
    };

    const handleSave = () => {
        if (!tenFile.trim()) {
            toast.warning('Vui lòng nhập tên file');
            return;
        }
        if (fileList.length === 0) {
            toast.warning('Vui lòng đính kèm ít nhất 1 file');
            return;
        }

        setIsSaving(true);
        const filePaths = fileList.map(f => f.filePath || f.url || f.name || f);
        const fileLuuTruValue = JSON.stringify(filePaths);

        const payload = {
            DotTuyenSinhId: dotTuyenSinhId,
            LoaiTuyenSinh: loaiTuyenSinh,
            TenFile: tenFile,
            FileLuuTru: fileLuuTruValue
        };

        if (currentRecord) {
            updateMutation.mutate(
                { id: currentRecord.id, payload },
                {
                    onSuccess: (res) => {
                        setIsSaving(false);
                        if (res.status === 200 || res.status === 204) {
                            toast.success('Cập nhật hồ sơ thành công');
                            setIsEditing(false);
                            refetch();
                        } else {
                            toast.error('Có lỗi xảy ra khi cập nhật hồ sơ');
                        }
                    },
                    onError: () => {
                        setIsSaving(false);
                        toast.error('Có lỗi xảy ra khi cập nhật hồ sơ');
                    }
                }
            );
        } else {
            createMutation.mutate(payload, {
                onSuccess: (res) => {
                    setIsSaving(false);
                    if (res.status === 200 || res.status === 201) {
                        toast.success('Thêm mới hồ sơ thành công');
                        setIsEditing(false);
                        refetch();
                    } else {
                        toast.error('Có lỗi xảy ra khi tạo mới hồ sơ');
                    }
                },
                onError: () => {
                    setIsSaving(false);
                    toast.error('Có lỗi xảy ra khi tạo mới hồ sơ');
                }
            });
        }
    };

    const renderFile = (pathStr) => {
        if (!pathStr) return <span className="text-muted">Không có file</span>;
        
        let paths = [];
        try {
            const parsed = JSON.parse(pathStr);
            if (Array.isArray(parsed)) {
                paths = parsed;
            } else {
                paths = [pathStr];
            }
        } catch(e) {
            paths = [pathStr];
        }

        return paths.map((actualPath, idx) => {
            const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
            const safePath = actualPath.startsWith('/') ? actualPath : `/${actualPath}`;
            const fileUrl = actualPath.startsWith('http') ? actualPath : `${baseUrl}${safePath}`;
            let name = actualPath.split('/').pop() || 'File';
            name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
            
            return (
                <div key={idx} className="mb-1">
                    <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm text-left" style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                        <i className="fas fa-paperclip mr-1"></i> {name}
                    </a>
                </div>
            );
        });
    };

    return (
        <Modal 
            show={isOpen} 
            onHide={onClose} 
            size="lg" 
            backdrop="static" 
            keyboard={false}
            centered
            style={{ zIndex: '9999' }}
        >
            <Modal.Header closeButton>
                <Modal.Title>Lưu trữ hồ sơ - {tenDot}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                {!isEditing ? (
                    <>
                        <div className="d-flex justify-content-end mb-3">
                            <Button variant="success" size="sm" onClick={handleAdd}>
                                <i className="fas fa-plus mr-1"></i> Thêm mới
                            </Button>
                        </div>
                        {isLoading ? (
                            <div className="text-center py-4">Đang tải dữ liệu...</div>
                        ) : (
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '50px' }}>STT</th>
                                        <th>Tên file</th>
                                        <th>File đính kèm</th>
                                        <th className="text-center" style={{ width: '120px' }}>Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center">Chưa có hồ sơ lưu trữ nào</td>
                                        </tr>
                                    ) : (
                                        items.map((item, index) => (
                                            <tr key={item.id}>
                                                <td className="text-center">{index + 1}</td>
                                                <td>{item.tenFile}</td>
                                                <td>{renderFile(item.fileLuuTru)}</td>
                                                <td className="text-center">
                                                    <Button variant="primary" size="sm" className="mr-2" title="Sửa" onClick={() => handleEdit(item)}>
                                                        <i className="fas fa-edit"></i>
                                                    </Button>
                                                    <Button variant="danger" size="sm" title="Xóa" onClick={() => handleDelete(item.id)}>
                                                        <i className="fas fa-trash"></i>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        )}
                    </>
                ) : (
                    <>
                        <Form.Group>
                            <Form.Label>Tên file <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                                type="text" 
                                placeholder="Nhập tên file" 
                                value={tenFile} 
                                onChange={(e) => setTenFile(e.target.value)} 
                            />
                        </Form.Group>
                        <DocumentInput
                            value={fileList}
                            multiple={true}
                            onFilesChange={setFileList}
                            labelTitle={<>File đính kèm <span className="text-danger">*</span></>}
                        />
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {isEditing ? (
                    <>
                        <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={isSaving}>
                            Hủy
                        </Button>
                        <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Đang lưu...' : 'Lưu lại'}
                        </Button>
                    </>
                ) : (
                    <Button variant="secondary" onClick={onClose}>
                        Đóng
                    </Button>
                )}
            </Modal.Footer>

            <ConfirmModal
                isOpen={openConfirmDelete}
                confirmType="delete"
                goBackAfterDelete={false}
                handleAccept={confirmDelete}
                handleCancel={() => setOpenConfirmDelete(false)}
                content="Bạn có chắc chắn muốn xóa hồ sơ này"
            />
        </Modal>
    );
};

export default HoSoLuuTruModal;
